import type {
  ActivityConfig,
  ActivityPackageKey,
  ActivityTimePeriod,
  PricingEntry,
  UserSegment
} from "@/views/power/components/VipPackageActivity/types";
import { promotion618Config } from "@/views/power/components/VipPackageActivity/configs/618";
import { springRenewalConfig } from "@/views/power/components/VipPackageActivity/configs/springRenewal";
import { CURRENT_POWER_ACTIVITY_TYPE } from "@/constants/activity";

const ACTIVITY_CONFIG_REGISTRY: Record<string, ActivityConfig> = {
  [springRenewalConfig.activityType]: springRenewalConfig,
  [promotion618Config.activityType]: promotion618Config
};

export interface ActivityPricingOptions {
  isShowActivity?: boolean;
  isParticipatedPro?: boolean;
  isParticipatedPlus?: boolean;
  activityConfig?: ActivityConfig;
  activitySegment?: UserSegment;
  vipType?: string;
  currentPackage?: string;
}

export interface UserSegmentSource {
  highestPackage: string;
  isMemberUser: boolean;
  isExpireUser: boolean;
}

/** 从用户状态解析 618 活动分层（供价格引擎与 composable 共用） */
export function resolveUserSegmentFromStore(userStore: UserSegmentSource): UserSegment {
  if (userStore.highestPackage === "ultra") return "activeUltra";

  if (userStore.isMemberUser && ["vip", "svip"].includes(userStore.highestPackage)) {
    return "activeVipSvip";
  }

  if (userStore.isExpireUser) return "expired";

  return "neverPaid";
}

/** 返回当前主推活动 config */
export function getDefaultActivityConfig(): ActivityConfig {
  return ACTIVITY_CONFIG_REGISTRY[CURRENT_POWER_ACTIVITY_TYPE] ?? springRenewalConfig;
}

export function normalizeActivityTimePeriod(timeMode: string): ActivityTimePeriod | null {
  if (timeMode === "halfSubscribe" || timeMode === "subscribe") return "subscribe";
  if (timeMode === "season") return "season";
  if (timeMode === "year" || timeMode === "year_v1") return "year";
  return null;
}

/** queryRights 活动购买记录（Store 消费层为 boolean） */
export interface ActivityParticipationFlags {
  activityPro?: boolean;
  activityPlus?: boolean;
  activityUpgradePlusMonth?: boolean;
  activityUpgradeUltraMonth?: boolean;
  activityUpgradePlusYear?: boolean;
  activityUpgradeUltraYear?: boolean;
}

type ParticipationFlagKey = keyof ActivityParticipationFlags;

/** 将 PackageMoneyOptions / Store 上的分散字段收敛为 flags */
export function buildActivityParticipationFlags(
  partial: Partial<ActivityParticipationFlags> & {
    isParticipatedPro?: boolean;
    isParticipatedPlus?: boolean;
  } = {}
): ActivityParticipationFlags {
  return {
    activityPro: partial.isParticipatedPro ?? partial.activityPro ?? false,
    activityPlus: partial.isParticipatedPlus ?? partial.activityPlus ?? false,
    activityUpgradePlusMonth: partial.activityUpgradePlusMonth ?? false,
    activityUpgradeUltraMonth: partial.activityUpgradeUltraMonth ?? false,
    activityUpgradePlusYear: partial.activityUpgradePlusYear ?? false,
    activityUpgradeUltraYear: partial.activityUpgradeUltraYear ?? false
  };
}

/**
 * 将 (周期, 目标套餐, 当前档位) 映射到对应 activity* 限购字段。
 * 未命中映射表示该 SKU 不受活动购买记录约束。
 */
export function resolveParticipationFlagKey(
  timeMode: string,
  packageKey: string | undefined,
  currentPackage?: string
): ParticipationFlagKey | null {
  if (!packageKey) return null;

  if (timeMode === "subscribe" && packageKey === "vip") {
    return "activityPro";
  }

  if (packageKey === "svip" && (timeMode === "halfSubscribe" || timeMode === "subscribe")) {
    if (currentPackage === "vip") return "activityUpgradePlusMonth";
    if (timeMode === "halfSubscribe") return "activityPlus";
    return null;
  }

  if (packageKey === "ultra" && timeMode === "subscribe") {
    if (currentPackage === "vip" || currentPackage === "svip") {
      return "activityUpgradeUltraMonth";
    }
    return null;
  }

  if (packageKey === "svip" && (timeMode === "year" || timeMode === "year_v1")) {
    if (currentPackage === "vip") return "activityUpgradePlusYear";
    return null;
  }

  if (packageKey === "ultra" && (timeMode === "year" || timeMode === "year_v1")) {
    if (currentPackage === "vip" || currentPackage === "svip") {
      return "activityUpgradeUltraYear";
    }
    return null;
  }

  return null;
}

/** 活动 SKU 已购买时跳过活动定价表，回落日常价 */
export function shouldSkipActivityPricing(
  timeMode: string,
  packageKey: string | undefined,
  flags: ActivityParticipationFlags,
  currentPackage?: string
): boolean {
  const flagKey = resolveParticipationFlagKey(timeMode, packageKey, currentPackage);
  if (!flagKey) return false;
  return !!flags[flagKey];
}

/** 月卡试用限购（兼容旧调用方，内部转调 shouldSkipActivityPricing） */
export function shouldSkipActivityPricingDueToParticipation(
  timeMode: string,
  vipType: string | undefined,
  isParticipatedPro: boolean,
  isParticipatedPlus: boolean,
  currentPackage?: string
): boolean {
  return shouldSkipActivityPricing(
    timeMode,
    vipType,
    buildActivityParticipationFlags({
      isParticipatedPro,
      isParticipatedPlus
    }),
    currentPackage
  );
}

/**
 * 在籍 VIP/SVIP 月度/年度 Tab 是否仍有可享的升级 6 折（用于角标「限时6折」聚合判断）
 */
export function hasActiveVipSvipUpgradePromoOnTab(
  tabValue: string,
  currentPackage: string,
  flags: ActivityParticipationFlags
): boolean {
  if (tabValue === "subscribe") {
    if (currentPackage === "vip" && !flags.activityUpgradePlusMonth) return true;
    if (
      (currentPackage === "vip" || currentPackage === "svip") &&
      !flags.activityUpgradeUltraMonth
    ) {
      return true;
    }
    return false;
  }

  if (tabValue === "year") {
    if (currentPackage === "vip" && !flags.activityUpgradePlusYear) return true;
    if (
      (currentPackage === "vip" || currentPackage === "svip") &&
      !flags.activityUpgradeUltraYear
    ) {
      return true;
    }
    return false;
  }

  return false;
}

export function resolveActivityPricingEntry(
  config: ActivityConfig | undefined,
  segment: UserSegment | undefined,
  timeMode: string,
  options: {
    packageKey?: string;
    currentPackage?: string;
  } = {}
): PricingEntry | null {
  if (!config || !segment) return null;

  const timePeriod = normalizeActivityTimePeriod(timeMode);
  if (!timePeriod) return null;

  const packageKey = options.packageKey as ActivityPackageKey | undefined;
  const packageEntry = packageKey ? config.packagePricingTable?.[timePeriod]?.[segment]?.[packageKey] : null;
  if (packageEntry) {
    const eligiblePackages = packageEntry.eligibleCurrentPackages;
    if (eligiblePackages?.length && !eligiblePackages.includes(options.currentPackage as ActivityPackageKey)) {
      return null;
    }
    return packageEntry;
  }

  return config.pricingTable[timePeriod]?.[segment] ?? null;
}

/**
 * 从活动定价表查询折扣系数。
 * fixed 类型返回 1（实际售价由 getPackageMoney 直接取 fixed value）；
 * 未命中返回 null，交给原有逻辑兜底。
 */
export function resolveActivityDiscount(
  config: ActivityConfig | undefined,
  segment: UserSegment | undefined,
  timeMode: string,
  options: {
    packageKey?: string;
    currentPackage?: string;
  } = {}
): number | null {
  const entry = resolveActivityPricingEntry(config, segment, timeMode, options);
  if (!entry) return null;
  if (entry.type === "fixed") return 1;
  return entry.value;
}

export interface ResolvedActivityPricingContext {
  config?: ActivityConfig;
  segment?: UserSegment;
  currentPackage?: string;
}

/**
 * 收敛活动定价上下文：调用方显式传入优先，否则在活动期内自动补齐。
 */
export function resolveActivityPricingContext(
  options: ActivityPricingOptions,
  userStore: UserSegmentSource
): ResolvedActivityPricingContext {
  if (!options.isShowActivity) return {};

  const config = options.activityConfig ?? getDefaultActivityConfig();
  const segment = options.activitySegment ?? resolveUserSegmentFromStore(userStore);
  const currentPackage = options.currentPackage ?? userStore.highestPackage;

  return { config, segment, currentPackage };
}
